import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { formatDateTime as formatDateTimeTz, formatDate as formatDateIso } from '../utils/datetime';
import { ArrowLeft, ArrowLeftRight, ChevronDown, Calendar, User, Building, Settings, RefreshCw, Edit, Copy, Database, Search, X, CheckCircle, AlertCircle, Download, Upload, UploadCloud, MonitorDown, FileText, ScrollText, ChevronUp, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import syncService from '../services/syncService';
import SyncConnectLogsModal from './shared/SyncConnectLogsModal';
import syncPaymentService from '../services/syncPaymentService';
import partnerSyncTemplatesService from '../services/partnerSyncTemplatesService';
import Card from './shared/Card';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import NotificationsPanel from './shared/NotificationsPanel';
import TaskSummaryStats from './shared/TaskSummaryStats';
import HubLogoMark from './shared/HubLogoMark';
import CopyButton from './shared/CopyButton';
import ContactBadge from './shared/ContactBadge';
import OxiDequeueAdmin from './OxiDequeueAdmin';
import { isConfigGroup, isExtractGroup, isProcessGroup, isScheduleGroup } from './shared/taskOrder';
import './SyncProjectDetailsPage.css';
import ProductSupportTab from './support/ProductSupportTab';
import DocsButton from './support/DocsButton';
import CollapsibleActions from './shared/CollapsibleActions';

// One side of the source→target pair, as a compact chip.
//
// This replaced a full-width 128px-tall card that showed the same three fields at 80px logos. The
// information is worth keeping visible — it is how you confirm at a glance you are on the right
// project — but it is reference detail, not the thing you came to the page to read, so it earns a
// strip rather than a panel.
// `badges` are that side's live heartbeats. They hang off the system they describe rather than living
// in a separate labelled box: the box repeated the SOURCE/TARGET word that this chip already carries,
// so the strip said "SOURCE" and "TARGET" twice and cost a second row to do it. Attached here, which
// side a heartbeat belongs to is shown by position instead of spelled out.
// One hop in the pipeline. Every participant gets its own card so the path reads as the chain it
// actually is, rather than a single source-to-target arrow that hides the two systems in the middle.
//
// The connector name is deliberately NOT a visible line: it read "Sync Connector" on both sides of
// every project, so it spent a line restating a constant. It lives in the tooltip, where it is still
// there for the projects whose connector name distinguishes something.
// `alert` (V613 status queries): { severity: 'ERROR' | 'WARNING', messages: [...] } — the box turns red or amber and
// says why, from what this project's SyncConnect last answered to the once-a-minute status queries.
const PipelineNode = ({ label, name, hint, image, icon: Icon, mark, badges, children, alert }) => (
  <div className={`sync-node${alert ? ` sync-node-alert sync-node-alert-${String(alert.severity).toLowerCase()}` : ''}`}>
    <div className="sync-node-head">
      {/* `mark` is a ready-made branded element (the Hub logo), so it is rendered as-is rather than
          dropped into the grey fallback tile - it carries its own colour and shape. */}
      {mark
        || (image
          ? <img src={image} alt="" className="sync-node-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          : <span className="sync-node-logo sync-node-logo-fallback"><Icon size={17} /></span>)}
      <span className="sync-node-text">
        <span className="sync-node-label">{label}</span>
        <span className="sync-node-name" title={hint || name}>{name}</span>
      </span>
    </div>
    {alert && (
      <ul className="sync-node-alerts">
        {alert.messages.map((m, i) => <li key={i}>{m}</li>)}
      </ul>
    )}
    {(badges || children) && (
      <div className="sync-node-foot">
        {badges}
        {children}
      </div>
    )}
  </div>
);

// Awaiting-payment block — replaces the project view when paid='N' && stripe_inv_id is set.
// Mirrors vault's "Awaiting invoice payment" panel: shows recipient/target/sent-on plus a single
// Resend Invoice button that re-sends to the original recipient.
const AwaitingPaymentBlock = ({ projectDetails, syncId, navigate, isSuperAdmin, onMarkedPaid }) => {
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [resending, setResending] = useState(false);
    const [resendMsg, setResendMsg] = useState(null);
    const [resendErr, setResendErr] = useState(null);
    const [marking, setMarking] = useState(false);
    const [markErr, setMarkErr] = useState(null);
    const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);

    const handleMarkPaid = () => setShowMarkPaidModal(true);

    const confirmMarkPaid = async () => {
        setShowMarkPaidModal(false);
        setMarking(true);
        setMarkErr(null);
        try {
            await syncPaymentService.markInvoicePaid(syncId);
            onMarkedPaid && onMarkedPaid();
        } catch (err) {
            setMarkErr(err.response?.data?.error?.message || err.message || 'Failed to mark paid');
        } finally {
            setMarking(false);
        }
    };

    useEffect(() => {
        if (!syncId) return;
        syncPaymentService.getInvoiceDetails(syncId)
            .then(res => setInvoiceDetails(res.data?.invoice || null))
            .catch(() => setInvoiceDetails(null));
    }, [syncId]);

    const handleResend = async () => {
        setResending(true);
        setResendMsg(null);
        setResendErr(null);
        try {
            const res = await syncPaymentService.resendInvoice({ syncProjectId: syncId });
            setResendMsg(`Invoice re-sent to ${res.data?.emailSentTo || invoiceDetails?.customerEmail || 'the customer'}.`);
        } catch (err) {
            setResendErr(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to resend invoice');
        } finally {
            setResending(false);
        }
    };

    const targetLabel = invoiceDetails?.targetType
        ? invoiceDetails.targetType.charAt(0).toUpperCase() + invoiceDetails.targetType.slice(1)
        : '—';

    return (
        <div className="sync-project-details-page">
            <div className="sync-details-container">
                <div className="breadcrumb">
                    <Link to="/sync" className="breadcrumb-link">Sync Projects</Link>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">{projectDetails.projectName || 'Project'}</span>
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1.5rem 1.75rem', background: '#fef3c7', border: '2px solid #fcd34d', borderRadius: '12px', textAlign: 'center', maxWidth: '640px', marginInline: 'auto' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }}>⏳</div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e1b4b', marginBottom: '4px' }}>Awaiting invoice payment</div>
                    <p style={{ margin: '0 0 1rem', color: '#78350f', fontSize: '0.9rem' }}>
                        Sync project processing will begin once the invoice is paid.
                    </p>
                    <div style={{ fontSize: '0.95rem', color: '#1e1b4b', lineHeight: 1.7 }}>
                        <div><strong>Sent to:</strong> {invoiceDetails?.customerEmail || '—'}</div>
                        <div><strong>Addressed to:</strong> {targetLabel}</div>
                        <div><strong>Sent on:</strong> {invoiceDetails?.createdAt ? new Date(invoiceDetails.createdAt).toLocaleString() : '—'}</div>
                        {invoiceDetails?.hostedInvoiceUrl && (
                            <a
                                href={invoiceDetails.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', marginTop: '0.5rem', color: '#4f46e5', fontWeight: 600, textDecoration: 'underline' }}
                            >
                                View Payment Link
                            </a>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={resending}
                        style={{
                            marginTop: '1.25rem',
                            background: resending ? '#86efac' : '#16a34a',
                            color: '#fff',
                            border: 'none',
                            padding: '0.6rem 1.25rem',
                            borderRadius: '6px',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            cursor: resending ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {resending ? 'Sending…' : 'Resend Invoice'}
                    </button>
                    {isSuperAdmin && (
                        <button
                            type="button"
                            onClick={handleMarkPaid}
                            disabled={marking}
                            title="Superadmin: bypass Stripe and mark as paid"
                            style={{
                                marginTop: '0.75rem',
                                marginLeft: '0.5rem',
                                background: marking ? '#fde68a' : '#fff',
                                color: '#b45309',
                                border: '1.5px solid #b45309',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: marking ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {marking ? 'Marking…' : '⚙️ Mark as paid (admin)'}
                        </button>
                    )}
                    {resendMsg && <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '6px', color: '#065f46', fontSize: '0.85rem' }}>{resendMsg}</div>}
                    {resendErr && <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem' }}>{resendErr}</div>}
                    {markErr && <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem' }}>{markErr}</div>}
                </div>

                <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
                    <Button variant="secondary" onClick={() => navigate('/sync')}>
                        <ArrowLeft className="icon" /> Back to Projects
                    </Button>
                </div>
            </div>

            {showMarkPaidModal && (
                <ConfirmModal
                    title="Mark as paid"
                    message="Mark this project as paid manually? This bypasses Stripe and immediately activates the project."
                    confirmLabel="Mark as paid"
                    cancelLabel="Cancel"
                    onConfirm={confirmMarkPaid}
                    onCancel={() => setShowMarkPaidModal(false)}
                />
            )}
        </div>
    );
};

// Generate + download the project's SyncConnect desktop client. Shown inside the Source box when the
// project has uses_sync_connect = 'Y'. Kicks off an async build then polls the download endpoint
// (which returns the presigned URL once the client is ready), mirroring vault's generate/download.
// HOW LONG THE CLIENT BUILD IS GIVEN, in one place.
//
// FIFTEEN MINUTES, not the vault client's five. This build compiles a .NET client and packages an
// installer, which takes longer than the vault's Go binary — a five-minute cut-off was abandoning
// builds that were still running and telling the user to try again, which starts the same slow build
// over. The poll count, the progress bar's slope and the timeout message are all derived from it, so
// changing the window is one number.
//
// Module scope rather than inside the component: they are constants, and a `const` in the body is
// rebuilt every render for an effect that must not re-run on every render.
const GENERATE_TIMEOUT_MS = 15 * 60 * 1000;
const POLL_EVERY_MS = 5000;
const ANIM_EVERY_MS = 250;

// offline: the project's template builds the OFFLINE client (synctemplates.syncconnect_offline, V610). It never contacts
// the hub, so it can neither collect an Auto-update nor send its logs — those actions are not offered.
const SyncConnectClientButton = ({ syncId, initialStatus, isSuperAdmin, offline = false }) => {
    const [status, setStatus] = useState(initialStatus || 'idle'); // idle|generating|completed
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    // The version just queued for the installed client, or null. Cleared on the next action.
    const [pushed, setPushed] = useState(null);
    const [logsOpen, setLogsOpen] = useState(false);
    // The four secondary actions, folded by default — see the chevron below for why.
    const [actionsOpen, setActionsOpen] = useState(false);
    // Shown only when the clipboard write is refused, so the link can still be moved by hand.
    const [linkFallback, setLinkFallback] = useState(null);
    // OFFLINE ONLY (V633): the project's builds as the download poll reports them — { latest: { buildNumber,
    // createdAt, succeededAt }, generating, templateChangedAt, outOfDate }. A site's upload is accepted only from latest.
    const [offlineBuild, setOfflineBuild] = useState(null);
    // True from the click on Regenerate until the build has actually been accepted. The screen flips to
    // the generating state immediately, which means for a moment it is showing 'generating' while the
    // project row still says 'completed' from the PREVIOUS build - so a poll landing in that window would
    // read ready:true and bounce Download/Copy link straight back. Polls are skipped until this clears.
    const startingRef = useRef(false);

    // Resolve current build state on mount (covers a reload while a build is in flight / done).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await syncService.getSyncConnectDownload(syncId);
                if (cancelled) return;
                if (data.offlineBuild !== undefined) setOfflineBuild(data.offlineBuild);
                if (data.ready) setStatus('completed');
                else if (data.status === 'generating') setStatus('generating');
                else if (data.status === 'failed') {
                    // Opened after a build failed: say so, and offer Generate again.
                    setStatus('idle');
                    setError(data.error || 'The last SyncConnect build failed — see the CodeBuild log, then generate again.');
                }
                else setStatus(data.status || 'idle');
            } catch (e) { /* keep current status */ }
        })();
        return () => { cancelled = true; };
    }, [syncId]);

    // While generating: poll for readiness and animate a progress bar over the same window (see the
    // timing constants above).
    useEffect(() => {
        if (status !== 'generating') { setProgress(0); return; }
        let polls = 0;
        const maxPolls = Math.round(GENERATE_TIMEOUT_MS / POLL_EVERY_MS);
        const timeoutLabel = `${Math.round(GENERATE_TIMEOUT_MS / 60000)} minutes`;
        const poll = setInterval(async () => {
            if (startingRef.current) return;   // build not accepted yet; the row still describes the old one
            polls += 1;
            try {
                const data = await syncService.getSyncConnectDownload(syncId);
                if (data.offlineBuild !== undefined) setOfflineBuild(data.offlineBuild);
                if (data.ready) { setProgress(100); setStatus('completed'); }
                // A FAILED BUILD ENDS THE WAIT. Only `ready` and the timeout were handled, so a build that
                // failed in its first minute kept the bar climbing for the whole timeout (GRUJA, 2026-10-01).
                else if (data.status === 'failed') {
                    setStatus('idle');
                    setError(data.error || 'The SyncConnect build failed — see the CodeBuild log, then generate again.');
                }
                else if (polls >= maxPolls) { setStatus('idle'); setError(`Generation timed out after ${timeoutLabel}. Please try again.`); }
            } catch (e) {
                if (polls >= maxPolls) { setStatus('idle'); setError(`Generation timed out after ${timeoutLabel}. Please try again.`); }
            }
        }, POLL_EVERY_MS);
        // THE BAR TRACKS THE WAIT, AND STOPS SHORT OF THE END. It used to fill in two minutes and then
        // sit at 100% for whatever was left, claiming the build was done while it was still running.
        // It now spans the timeout and caps at 95%; only a build that actually finished reaches 100.
        const increment = 95 / (GENERATE_TIMEOUT_MS / ANIM_EVERY_MS);
        const anim = setInterval(() => setProgress(p => Math.min(p + increment, 95)), ANIM_EVERY_MS);
        return () => { clearInterval(poll); clearInterval(anim); };
    }, [status, syncId]);

    const handleGenerate = async () => {
        // Switch to the generating view on the CLICK, not on the response. Download and Copy link hand out
        // the previous installer, which a regenerate is about to replace at the same S3 key - so for as
        // long as they stay on screen they are offering a file that is on its way out. The build request
        // takes a moment to be accepted, and that moment was long enough to use them.
        const previous = status;
        startingRef.current = true;
        setBusy(true); setError(null); setProgress(0);
        setStatus('generating'); // the effect above takes over polling + progress
        try {
            await syncService.generateSyncConnect(syncId);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to start build');
            // Nothing was rebuilt, so the previous client is still the current one - go back to offering
            // it rather than dropping the user on the Generate button with a working installer in place.
            setStatus(previous === 'completed' ? 'completed' : 'idle');
        } finally {
            startingRef.current = false;
            setBusy(false);
        }
    };

    const handleDownload = async () => {
        setBusy(true); setError(null);
        try {
            const data = await syncService.getSyncConnectDownload(syncId);
            if (data.ready && data.presignedUrl) { window.location.href = data.presignedUrl; }
            else { setStatus(data.status || 'generating'); }
        } catch (e) {
            setError(e.response?.data?.message || 'Download not ready');
        } finally { setBusy(false); }
    };

    const btnBase = {
        display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.4rem 0.7rem',
        borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        border: '1px solid transparent',
    };

    // AUTO-UPDATE. One call, nothing to fill in — the build supplies the version, the URL and the hash.
    //
    // THE RESULT IS NOT A SUCCESS MESSAGE, and it should not read like one: the client has to ask for
    // the message before anything happens, and it asks on its own poll interval. Saying "updated" here
    // would be a claim about a machine this screen has not heard from yet.
    const handleAutoUpdate = async () => {
        setBusy(true); setError(null); setPushed(null);
        try {
            const res = await syncService.updateSyncConnect(syncId);
            // NOT "UPDATED". The client has to ask for the message before anything happens, and it asks
            // on its own poll interval — so this is a statement about the queue, not about the machine.
            // Saying "updated" would be a claim about a server this screen has not heard from yet.
            setPushed(res.version || 'the current build');
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message
                || 'The update could not be queued');
        } finally {
            setBusy(false);
        }
    };

    // LOGS — the files from the property's own server, without remoting onto it. ONE button, offered whatever
    // state the hub's BUILD is in: the logs belong to the INSTALLED client, and a client installed before the hub kept
    // a build record (or whose build expired) polls perfectly well with this card still saying Generate — it must not
    // take a rebuild to read its logs. SUPERADMIN ONLY: a SyncConnect log is a verbatim record of the property's OXI
    // traffic (guest names, reservation detail, credentials in message headers), a wider disclosure than the screen
    // around it, and Fetch queues work on the customer's machine. Not on the offline edition, which never polls.
    const logsButton = isSuperAdmin && !offline ? (
        <button
            className="sa-only"
            style={{ ...btnBase, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}
            onClick={() => setLogsOpen(true)}
            title="Log files from this property's SyncConnect — and a Fetch button to ask it for the latest"
        >
            <ScrollText size={13} /> Logs
        </button>
    ) : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px' }}>
            {/* Mounted here rather than inside the completed branch: the dialog is about the INSTALLED
                client, which exists whatever state a rebuild is in — closing it should not depend on a
                build finishing. */}
            <SyncConnectLogsModal isOpen={logsOpen} onClose={() => setLogsOpen(false)} syncId={syncId} />
            {status === 'completed' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <button style={{ ...btnBase, background: '#4a6cf7', color: '#fff' }} onClick={handleDownload} disabled={busy}>
                        <Download size={13} /> {busy ? 'Preparing…' : 'Download'}
                    </button>
                    {/* EVERYTHING ELSE FOLDS BEHIND THIS.
                        Download is the one thing anybody needs from this card day to day; the other four
                        are things done once, or during an incident. Six buttons wrapping onto three rows
                        made the common action the hardest to pick out — and a card that is mostly
                        superadmin controls reads as a control panel rather than a download.
                        THE CHEVRON IS A REAL CONTROL, labelled and with aria-expanded, not a glyph that
                        has to be discovered by hovering. */}
                    <button
                        type="button"
                        style={{ ...btnBase, background: 'none', color: '#6b7280', padding: '4px 6px' }}
                        onClick={() => setActionsOpen(o => !o)}
                        aria-expanded={actionsOpen}
                        aria-label={actionsOpen ? 'Hide the other actions' : 'Show the other actions'}
                        title={actionsOpen ? 'Hide the other actions' : (offline ? 'Copy link, Regenerate' : 'Copy link, Regenerate, Auto-update, Logs')}
                    >
                        {actionsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {actionsOpen && <>
                    {/* The installer has to end up on the OXI machine, which is usually not the machine
                        the portal is open on and often cannot reach the portal at all. So the link has to
                        travel — and it travels through a remote-desktop clipboard, which TRUNCATES.
                        A presigned S3 URL is ~900 characters; what arrived on the far end looked like a
                        URL and failed with a signature error that named nothing, so it read as a broken
                        build rather than a broken paste.
                        The short link is twelve characters on the sync API's own domain, redirecting to a
                        freshly signed URL when it is followed. It survives a clipboard, and it can be read
                        down a phone line. The long URL stays as the fallback for a client built before
                        short links existed. */}
                    <CopyButton
                        label="Copy link"
                        title="Copy a short download link to use on the OXI machine. Works from any network and expires in 24 hours."
                        getValue={async () => {
                            // A NEW short link on every click: it redirects to a URL signed when it is followed,
                            // and its own 24 hours start now (syncconnect.js download ?fresh=1).
                            const data = await syncService.getSyncConnectDownload(syncId, { fresh: true });
                            if (data && data.ready === false) throw new Error(data.error || 'The installer is not available');
                            const link = data?.shortUrl || data?.presignedUrl;
                            if (!link) throw new Error('no download link available');
                            return link;
                        }}
                        onFallback={(url, err) => {
                            // A link that could not be put on the clipboard is shown to copy by hand; no link at all
                            // (the installer expired) is said as the error it is.
                            if (url) setLinkFallback(url);
                            else setError(err?.message || 'No download link is available');
                        }}
                    />
                    {/* Once a client exists there is otherwise no way back to a build: the Generate
                        button is replaced by Download and stays replaced. Superadmin-only because a
                        rebuild replaces the installer at the project's fixed S3 key, so the link a
                        customer is part-way through using changes underneath them. */}
                    {isSuperAdmin && (
                        <button
                            className="sa-only"
                            style={{ ...btnBase, background: '#fff', color: '#4a6cf7', border: '1px solid #4a6cf7' }}
                            onClick={handleGenerate}
                            disabled={busy}
                            title="Rebuild the client from the project's current settings, replacing the installer at this project's download link"
                        >
                            <RefreshCw size={13} /> {busy ? 'Starting…' : 'Regenerate'}
                        </button>
                    )}
                    {/* AUTO-UPDATE — install the built client on the machine that is already running
                        one, instead of somebody carrying the MSI to it.
                        WHY A BUTTON AND NOT AUTOMATIC ON REBUILD. Installing restarts the service on a
                        property's own server, and a rebuild is not the same decision as "restart their
                        site now". Regenerating to inspect a setting would otherwise bounce every
                        installed client that shares the change.
                        NOTHING IS ASKED FOR: the version, the installer URL and its SHA-256 all come
                        from the build. Superadmin-only, for the same reason Regenerate is — it acts on
                        a machine the customer owns. */}
                    {isSuperAdmin && !offline && (
                        <button
                            className="sa-only"
                            style={{ ...btnBase, background: '#fff', color: '#7c3aed', border: '1px solid #ddd6fe' }}
                            onClick={handleAutoUpdate}
                            disabled={busy}
                            title="Send this build to the SyncConnect already installed at the property. It collects it on its next poll, verifies the installer and restarts as part of the upgrade."
                        >
                            <UploadCloud size={13} /> {busy ? 'Sending…' : 'Auto-update'}
                        </button>
                    )}
                    {/* LOGS — the files from the property's own server, without remoting onto it.
                        SUPERADMIN ONLY. A SyncConnect log is a verbatim record of the property's OXI
                        traffic: guest names, reservation detail, and the credentials in a message
                        header. That is a wider disclosure than the screen around it, and Fetch inside
                        the dialog also queues work on the customer's own machine. */}
                    {logsButton}
                    </>}
                    {pushed && (
                        <div style={{ flexBasis: '100%', marginTop: '4px', fontSize: '0.72rem', color: '#5b21b6' }}>
                            Queued <strong>{pushed}</strong> for this property&rsquo;s SyncConnect. It
                            collects the message on its next poll, verifies the installer, and restarts as
                            part of the upgrade — the version here updates once it reports back.
                        </div>
                    )}
                    {linkFallback !== null && (
                        <div style={{ flexBasis: '100%', marginTop: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '3px' }}>
                                {linkFallback
                                    ? 'Could not reach the clipboard - select and copy this link (valid 1 hour):'
                                    : 'Could not produce a download link. Try Download, or regenerate the client.'}
                            </div>
                            {linkFallback && (
                                <input
                                    readOnly
                                    value={linkFallback}
                                    onFocus={(e) => e.target.select()}
                                    onClick={(e) => e.target.select()}
                                    style={{
                                        width: '100%', fontSize: '0.7rem', padding: '4px 6px',
                                        border: '1px solid #d1d5db', borderRadius: '4px',
                                        fontFamily: 'ui-monospace,Consolas,Menlo,monospace',
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            ) : status === 'generating' ? (
                // 140, not 190: the card is now one of four equal-width columns, so on a narrower
                // window a 190px floor would push its own card wider than its peers.
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '140px', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280' }}>
                        {/* Names what is being generated. On its own, "Generating..." sat in the header
                            with no indication it referred to the SyncConnect client. */}
                        <RefreshCw size={13} className="spinning" /> Generating SyncConnect…
                        <span style={{ marginLeft: 'auto', color: '#4F7FFF' }}>{Math.round(progress)}%</span>
                    </div>
                    <div style={{ width: '100%', height: '8px', background: '#e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(135deg, #4F7FFF 0%, #3b66cc 100%)', borderRadius: '6px', transition: 'width 0.1s linear' }} />
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <button style={{ ...btnBase, background: '#4a6cf7', color: '#fff' }} onClick={handleGenerate} disabled={busy}>
                        <Download size={13} /> {busy ? 'Starting…' : 'Generate SyncConnect'}
                    </button>
                    {logsButton}
                </div>
            )}
            {offline && offlineBuild && (() => {
                const day = (v) => (v ? new Date(v).toLocaleDateString() : '');
                const latest = offlineBuild.latest;
                return (
                    <div style={{ fontSize: '0.72rem', color: '#4b5563', lineHeight: 1.4 }}>
                        {latest
                            ? <>SyncConnect build <strong>{latest.buildNumber}</strong> · generated {day(latest.succeededAt || latest.createdAt)} — uploads are accepted only from this build</>
                            : <>No SyncConnect build has succeeded yet — uploads are refused until one has</>}
                        {offlineBuild.generating && <> · build {offlineBuild.generating.buildNumber} generating</>}
                        {offlineBuild.outOfDate && latest && (
                            <div style={{ color: '#b45309', fontWeight: 600 }}>
                                SyncConnect is out of date: the template changed on {day(offlineBuild.templateChangedAt)}, after build {latest.buildNumber}.
                                Generate SyncConnect again and reinstall it at the site.
                            </div>
                        )}
                    </div>
                );
            })()}
            {error && <span style={{ fontSize: '0.7rem', color: '#dc2626' }}>{error}</span>}
        </div>
    );
};

// Phase classifiers imported from ./shared/taskOrder (shared with TaskConfigurationPage prev/next).

// Tabs no one but a superadmin may open. Logs are the raw message payloads exchanged with the source and
// target systems — guest names, card tokens, whole reservations — which is support material, not
// something a property's own users are given; Backups are served by the admin gateway and were already
// restricted. Named once here so the guard, the tab bar and the panels cannot drift apart.
const SUPERADMIN_TABS = ['logs', 'backups', 'instructions'];

const SyncProjectDetailsPage = () => {
  const navigate = useNavigate();
  const { syncId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, isPartner, solePartnerId, loading: authLoading } = useAuth();
  const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
  const invoicePrivilege = (isSuperAdmin || isPartner) ? 'partner' : isChainAdmin ? 'chain' : 'hotel';

  const [projectDetails, setProjectDetails] = useState(null);
  const [taskGroups, setTaskGroups] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['tasks', 'configuration', 'process', 'schedule', 'logs', 'notifications', 'backups', 'instructions', 'support'].includes(tab) ? tab : 'tasks';
  });

  // Backups tab — archived-index backups + restore
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupsLoaded, setBackupsLoaded] = useState(false);
  const [backupsError, setBackupsError] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  // Notifications tab — shared NotificationsPanel in persistence mode (commits immediately).
  const [notif, setNotif] = useState({ emails: [], teamsWebhook: null });
  const notifPersistence = {
    addEmail:    ({ email, source }) => syncService.addNotificationEmail(syncId, { email, source }),
    removeEmail: (id)                => syncService.removeNotificationEmail(syncId, id),
    saveWebhook: (url)               => syncService.saveNotificationWebhook(syncId, url),
  };

  // Project details expand
  const [isProjectDetailsExpanded, setIsProjectDetailsExpanded] = useState(false);
  const [expandedTaskGroups, setExpandedTaskGroups] = useState(new Set());

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [logFilters, setLogFilters] = useState({ transactionId: '', lambda: '', direction: '' });
  const [expandedLog, setExpandedLog] = useState(null);

  // Payload dialog state
  const [payloadDialog, setPayloadDialog] = useState(null); // { content: string }

  // Resolved template source
  const [resolvedLevel, setResolvedLevel] = useState(null);
  const [resolvedTemplateName, setResolvedTemplateName] = useState(null);

  // Extract tab search
  const [extractTaskSearch, setExtractTaskSearch] = useState('');

  // Per-task reconciliation summaries shown inline on each extraction task card (uuid -> summary array)
  const [taskSummaries, setTaskSummaries] = useState({});
  // Auto-refresh those summaries every 5s while on the Extraction tab. On by default (HIL-87); the
  // hook pauses it while the tab is hidden and remembers it if a user switches it off.
  // Show only tasks that have had messages/records flow through them (off by default = show all)
  const [onlyWithMessages, setOnlyWithMessages] = useState(false);
  // Manual tasks are hidden by default: they never produce extraction records, so on a screen for
  // watching data move they are permanently empty rows between the ones that change.
  const [showManualTasks, setShowManualTasks] = useState(false);

  // SPR init state
  const [sprInitLoading, setSprInitLoading] = useState(false);
  const [sprInitResult, setSprInitResult] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);

  // Reset scroll to the top when a project opens — otherwise navigating from the bottom of the
  // /sync list carries that scroll position onto the detail page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [syncId]);

  useEffect(() => {
    if (syncId) loadProjectDetails();
  }, [syncId]);

  // Load notification recipients for the Notifications tab.
  useEffect(() => {
    if (!syncId) return;
    syncService.listNotificationEmails(syncId)
      .then(data => setNotif(n => ({ ...n, emails: (data.emails || []).map(e => ({ id: e.id, email: e.email, source: e.source })) })))
      .catch(() => { /* non-fatal */ });
  }, [syncId]);

  // Keep the panel's Teams webhook in sync with the loaded project.
  useEffect(() => {
    setNotif(n => ({ ...n, teamsWebhook: projectDetails?.notificationTeamsWebhook || null }));
  }, [projectDetails?.notificationTeamsWebhook]);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await syncService.getSyncProjectDetails(syncId);
      if (data.projectDetails && Object.keys(data.projectDetails).length > 0) {
        setProjectDetails(data.projectDetails);
      }
      setTaskGroups(data.taskGroups || []);
      setProjectTasks(data.projectTasks || []);
    } catch (err) {
      console.error('Error loading project details:', err);
      setError('Failed to load project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch a reconciliation summary for every task shown on the Extraction tab so each card can
  // display its own scope / in-flight / processed / errors / total / progress (same widget as the
  // task configuration page). Config / process / schedule tasks are excluded — they have no summary.
  // force=true re-fetches every task (used by auto-refresh); otherwise only tasks not yet loaded.
  const loadExtractionSummaries = useCallback((force = false) => {
    if (!projectTasks.length || !taskGroups.length) return;
    const extractGroupNames = new Set(
      taskGroups
        .filter(g => !isConfigGroup(g.groupName) && !isProcessGroup(g.groupName) && !isScheduleGroup(g.groupName))
        .map(g => g.groupName)
    );
    projectTasks
      .filter(t => extractGroupNames.has(t.taskGroup))
      .forEach(t => {
        const uuid = t.syncProjectTaskUuid;
        if (!force && taskSummaries[uuid] !== undefined) return; // already fetched
        syncService.getTaskSummary(syncId, uuid)
          .then(data => setTaskSummaries(prev => ({ ...prev, [uuid]: Array.isArray(data) ? data : [] })))
          .catch(() => setTaskSummaries(prev => ({ ...prev, [uuid]: [] })));
      });
  }, [projectTasks, taskGroups, syncId]); // eslint-disable-line

  // Initial load when the Extraction tab is opened.
  useEffect(() => {
    if (activeTab === 'tasks') loadExtractionSummaries(false);
  }, [activeTab, loadExtractionSummaries]);

  // Only polls on the Extraction tab — `enabled` gates it, so leaving the tab stops the queries without
  // the user's toggle being changed under them.
  const [autoRefreshSummaries, setAutoRefreshSummaries] = useAutoRefresh(
    'sync:autoRefreshSummaries', () => loadExtractionSummaries(true), 5000,
    { enabled: activeTab === 'tasks' });

  useEffect(() => {
    if (!projectDetails) return;
    const partnerId = projectDetails.partnerId || selectedPartner || solePartnerId;
    const hotelId = projectDetails.hotelId;
    const syncTemplateId = projectDetails.syncTemplateId;
    if (!partnerId || !hotelId || !syncTemplateId) return;
    partnerSyncTemplatesService.resolve(partnerId, hotelId, syncTemplateId)
      .then(result => {
        setResolvedLevel(result.level);
        setResolvedTemplateName(result.name || null);
      })
      .catch(() => {});
  }, [projectDetails]); // eslint-disable-line

  // --- OXI heartbeat indicators (enqueue / dequeue last contact) ---
  const [contact, setContact] = useState({ enqueueAge: null, dequeueAge: null, at: Date.now() });

  // Capture ages from each full project load (initial load + manual Refresh)
  // The status-query outcomes (V613), refreshed with the contact ages below so a box turns red without a reload.
  const [statusChecks, setStatusChecks] = useState(null);
  useEffect(() => {
    if (projectDetails) setStatusChecks(projectDetails.syncConnectChecks || null);
  }, [projectDetails]);

  useEffect(() => {
    if (!projectDetails) return;
    setContact({
      enqueueAge: projectDetails.enqueueLastContactAgeSec ?? null,
      dequeueAge: projectDetails.dequeueLastContactAgeSec ?? null,
      at: Date.now(),
    });
  }, [projectDetails]);

  // Poll for fresh contact ages without toggling the page spinner
  useEffect(() => {
    if (!syncId) return;
    const id = setInterval(async () => {
      try {
        const data = await syncService.getSyncProjectDetails(syncId);
        const pd = data.projectDetails || {};
        setContact({
          enqueueAge: pd.enqueueLastContactAgeSec ?? null,
          dequeueAge: pd.dequeueLastContactAgeSec ?? null,
          at: Date.now(),
        });
        setStatusChecks(pd.syncConnectChecks || null);
      } catch (e) { /* keep last known values */ }
    }, 20000);
    return () => clearInterval(id);
  }, [syncId]);

  // An age measured at `contact.at` becomes an absolute time, which the shared badge counts forward from.
  const contactAtMs = (baseAge) => (baseAge == null ? null : contact.at - baseAge * 1000);

  // The target system's business date, as last read by the ohip-check-busines-date scheduled task.
  //
  // Shown ONLY when the task has actually run for this project — an empty slot says "not checked",
  // which is honest; a fabricated "today" would say the night audit is fine when nothing has looked.
  //
  // Colour is judged against the CALENDAR date, not against when we last checked, because that is the
  // question being asked: has the property's night audit run? One day behind is normal for most of the
  // day (the audit for the 14th runs late on the 14th), two or more is a stall.
  const renderBusinessDateBadge = () => {
    const bd = projectDetails.targetBusinessDate;
    if (!bd) return null;
    const today = new Date().toISOString().split('T')[0];
    const lagDays = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${bd}T00:00:00Z`)) / 86400000);
    const behind = Number.isFinite(lagDays) && lagDays >= 2;
    // A check that has itself stopped running makes the date meaningless, so say so rather than
    // colouring a value nobody has refreshed in days.
    const ageSec = projectDetails.targetBusinessDateAgeSec;
    const stale = ageSec != null && ageSec > 60 * 60 * 36;
    const checkedText = projectDetails.targetBusinessDateCheckedAt
      ? formatDateTimeTz(projectDetails.targetBusinessDateCheckedAt)
      : 'unknown';
    return (
      <span
        className="sync-dot"
        title={`Target business date ${bd}${Number.isFinite(lagDays) ? ` — ${lagDays} day(s) behind today (${today})` : ''}. Last checked ${checkedText}.${behind ? ' The night audit has not run.' : ''}`}
      >
        <Calendar className="sync-dot-icon" size={13} strokeWidth={2.25} />
        <span className="sync-dot-label">Business date</span>
        <span className={`sync-dot-state ${stale ? 'is-never' : behind ? 'is-behind' : 'is-live'}`}>
          <span className="sync-dot-led" />
          {bd}
        </span>
      </span>
    );
  };

  // The project's cutover date, on the Platform (hub) card.
  //
  // It belongs to the hub rather than to either system: it is the date THIS PROJECT switches over, not
  // a property of the source or the target, and both sides are read against it.
  //
  // Shown as a plain badge with no LED, because there is nothing live about it — the coloured dot on the
  // heartbeat badges means "is this talking right now", and reusing it here would imply a health state
  // that a fixed date does not have.
  const renderCutoverBadge = () => {
    if (!projectDetails.projectCutoverDate) return null;
    return (
      <span className="sync-dot" title="Project cutover date — reservations departing before this date are not synced">
        <Calendar className="sync-dot-icon" size={13} strokeWidth={2.25} />
        <span className="sync-dot-label">Cutover</span>
        <span className="sync-dot-state sync-dot-plain">{formatDateIso(projectDetails.projectCutoverDate)}</span>
      </span>
    );
  };

  // One enqueue/dequeue badge group per side (source / target). Each badge only shows when the
  // connector's marketplace has_enqueue / has_dequeue flag is 'Y'. A side with neither renders nothing.
  // "To HCONNECT" = data arriving into HCONNECT (Upload, enqueue heartbeat); "From HCONNECT" = data
  // leaving HCONNECT (Download, dequeue heartbeat). The target connector's flags are inverted relative
  // to the source: target has_enqueue means "From HCONNECT" and has_dequeue means "To HCONNECT".
  const renderContactBadges = (sideLabel, hasEnqueue, hasDequeue, invert = false) => {
    if (hasEnqueue !== 'Y' && hasDequeue !== 'Y') return null;
    const showTo   = invert ? hasDequeue === 'Y' : hasEnqueue === 'Y';
    const showFrom = invert ? hasEnqueue === 'Y' : hasDequeue === 'Y';
    // Target swaps the directional icons too: To = Download, From = Upload.
    const ToIcon   = invert ? Download : Upload;
    const FromIcon = invert ? Upload : Download;
    // Labels are "To"/"From", not "To HCONNECT"/"From HCONNECT" — the other end of the arrow is the
    // hub on every one of these, so the word was four repetitions of the same constant across the
    // strip. The full phrase stays in the tooltip, along with the exact age.
    return (
      <>
        {showTo   && <ContactBadge icon={ToIcon}   label="To"   title={`${sideLabel} to HCONNECT`}   lastContactMs={contactAtMs(contact.enqueueAge)} />}
        {showFrom && <ContactBadge icon={FromIcon} label="From" title={`${sideLabel} from HCONNECT`} lastContactMs={contactAtMs(contact.dequeueAge)} />}
      </>
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadProjectDetails();
    if (activeTab === 'logs' && logsLoaded) {
      await loadLogs(logFilters);
    }
    setRefreshing(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === projectDetails.projectStatus) return;
    try {
      setUpdatingStatus(true);
      await syncService.updateProjectStatus(syncId, newStatus);
      setProjectDetails(prev => ({ ...prev, projectStatus: newStatus }));
    } catch (err) {
      // Surface the server message (e.g. the OHIP credential-in-use conflict naming the other project).
      setError(err.response?.data?.message || 'Failed to update project status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const loadLogs = useCallback(async (filters = {}) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const params = {};
      if (filters.transactionId) params.transactionId = filters.transactionId;
      if (filters.lambda) params.lambda = filters.lambda;
      if (filters.direction) params.direction = filters.direction;
      const result = await syncService.getLogs(syncId, params);
      setLogs(result.logs || []);
      setLogsLoaded(true);
    } catch (err) {
      setLogsError('Failed to load logs. Please try again.');
    } finally {
      setLogsLoading(false);
    }
  }, [syncId]);

  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    setBackupsError(null);
    try {
      const result = await syncService.getBackups(syncId);
      setBackups(result.backups || []);
      setBackupsLoaded(true);
    } catch (err) {
      setBackupsError('Failed to load backups. Please try again.');
    } finally {
      setBackupsLoading(false);
    }
  }, [syncId]);

  const handleRestoreBackup = async (backup) => {
    try {
      setRestoringId(backup.id);
      await syncService.restoreBackup(syncId, backup.id);
      await loadBackups();
      await loadProjectDetails();
    } catch (err) {
      setBackupsError('Failed to restore backup. Please try again.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
    if (tab === 'logs' && !logsLoaded) {
      loadLogs(logFilters);
    }
    if (tab === 'backups' && !backupsLoaded) {
      loadBackups();
    }
  };

  // A superadmin-only tab reached by URL (?tab=logs — a pasted link, a bookmark, or the tab the page was
  // last left on, which is written to the query string on every change) lands on a panel that renders
  // nothing for anyone else. Bounced to the default tab instead, and only once auth has resolved:
  // isSuperAdmin is false while the session is still loading, so acting earlier would throw a real
  // superadmin off their own link.
  useEffect(() => {
    if (authLoading || isSuperAdmin) return;
    if (SUPERADMIN_TABS.includes(activeTab)) handleTabChange('tasks');
  }, [authLoading, isSuperAdmin, activeTab]); // eslint-disable-line

  // Render tasks tab: extract/other groups only (config moved to Configuration tab)
  const renderTasksTab = () => {
    const extractAll = [...extractGroups, ...otherGroups];
    if (extractAll.length === 0) return <div className="tab-empty">No tasks found.</div>;
    return (
      <div className="tasks-section">
        <div className="tasks-section-header">
          <div className="task-search-bar" style={{ marginBottom: 0, flex: 1 }}>
            <Search className="task-search-icon" size={16} />
            <input
              type="text"
              className="task-search-input"
              placeholder="Search tasks..."
              value={extractTaskSearch}
              onChange={(e) => setExtractTaskSearch(e.target.value)}
            />
            {extractTaskSearch && (
              <button className="task-search-clear" onClick={() => setExtractTaskSearch('')}><X size={14} /></button>
            )}
          </div>
          <label className="auto-refresh-toggle" title="Show only tasks that have had messages/records go through them">
            <span className="auto-refresh-label">Only with messages</span>
            <button
              type="button"
              role="switch"
              aria-checked={onlyWithMessages}
              className={`auto-refresh-switch${onlyWithMessages ? ' on' : ''}`}
              onClick={() => setOnlyWithMessages(v => !v)}
            >
              <span className="auto-refresh-knob" />
            </button>
          </label>
          <label className="auto-refresh-toggle" title="Show tasks performed by hand in the target system. They produce no extraction records, so they are hidden here by default.">
            <span className="auto-refresh-label">Show manual tasks</span>
            <button
              type="button"
              role="switch"
              aria-checked={showManualTasks}
              className={`auto-refresh-switch${showManualTasks ? ' on' : ''}`}
              onClick={() => setShowManualTasks(v => !v)}
            >
              <span className="auto-refresh-knob" />
            </button>
          </label>
          <label className="auto-refresh-toggle" title="Auto-refresh task summaries every 5 seconds">
            <span className="auto-refresh-label">Auto refresh</span>
            <button
              type="button"
              role="switch"
              aria-checked={autoRefreshSummaries}
              className={`auto-refresh-switch${autoRefreshSummaries ? ' on' : ''}`}
              onClick={() => setAutoRefreshSummaries(v => !v)}
            >
              <span className="auto-refresh-knob" />
            </button>
          </label>
        </div>
        {renderConfigTaskGroups(extractAll, 'tasks', extractTaskSearch) || (
          <div className="tab-empty">No tasks match your search.</div>
        )}
      </div>
    );
  };

  const handleLogSearch = (e) => {
    e.preventDefault();
    loadLogs(logFilters);
  };

  const handlePurgeLogs = async () => {
    if (!purgeConfirm) { setPurgeConfirm(true); return; }
    setPurgeLoading(true);
    setPurgeConfirm(false);
    try {
      await syncService.purgeLogs(syncId);
      setLogs([]);
      setLogsLoaded(false);
    } catch (err) {
      console.error('Purge logs failed:', err);
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleSprInit = async () => {
    setSprInitLoading(true);
    setSprInitResult(null);
    try {
      const result = await syncService.initSpr(syncId);
      setSprInitResult({ success: true, data: result });
    } catch (err) {
      setSprInitResult({ success: false, message: err.message || 'Failed to initialise SPR infrastructure' });
    } finally {
      setSprInitLoading(false);
    }
  };

  const formatXml = (xml) => {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    xml.replace(/>\s*</g, '>\n<').split('\n').forEach(line => {
      line = line.trim();
      if (!line) return;
      if (line.match(/^<\/[^>]+>/)) indent = Math.max(0, indent - 1);
      formatted += tab.repeat(indent) + line + '\n';
      if (line.match(/^<[^/?!][^>]*[^/]>$/) || line.match(/^<[^/?!][^>]*[^/]>.*<\/[^>]+>$/) === null && line.match(/^<[^/?!][^>]*>$/) && !line.match(/^<[^>]+\/>$/) && !line.match(/<\/[^>]+>$/)) indent++;
    });
    return formatted.trim();
  };

  const handleViewPayload = (e, payloadB64) => {
    e.stopPropagation();
    try {
      const decoded = atob(payloadB64);
      try {
        const parsed = JSON.parse(decoded);
        const formatted = JSON.stringify(parsed, null, 2);
        setPayloadDialog({ raw: decoded, formatted, content: formatted, isPretty: true, type: 'json' });
      } catch {
        const isXml = decoded.trimStart().startsWith('<');
        if (isXml) {
          try {
            const formatted = formatXml(decoded);
            setPayloadDialog({ raw: decoded, formatted, content: formatted, isPretty: true, type: 'xml' });
          } catch {
            setPayloadDialog({ raw: decoded, formatted: decoded, content: decoded, isPretty: false, type: 'xml' });
          }
        } else {
          setPayloadDialog({ raw: decoded, formatted: decoded, content: decoded, isPretty: false, type: 'text' });
        }
      }
    } catch {
      setPayloadDialog({ raw: payloadB64, formatted: payloadB64, content: payloadB64, isPretty: false, type: 'text' });
    }
  };

  const togglePayloadFormat = () => {
    setPayloadDialog(prev => {
      const showRaw = prev.isPretty;
      return { ...prev, content: showRaw ? prev.raw : prev.formatted, isPretty: !showRaw };
    });
  };

  const toggleTaskGroup = (groupName) => {
    const newExpanded = new Set(expandedTaskGroups);
    if (newExpanded.has(groupName)) newExpanded.delete(groupName);
    else newExpanded.add(groupName);
    setExpandedTaskGroups(newExpanded);
  };

  const getTasksByGroup = (groupName) => projectTasks.filter(task => task.taskGroup === groupName);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (status) => {
    if (!status) return status;
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Both of these delegate to the shared formatters rather than calling toLocale* themselves: the
  // en-US/en-GB month-name formats they used ("4 Sept 2026, 18:03:32") are ambiguous across the
  // regions this platform is used in and carry no timezone, and the long form was what pushed the
  // last-message stamp wide enough to squeeze the stats table on the Extraction tab. See
  // utils/datetime.js for the two rules. The placeholders are kept as they were.
  const formatDate = (dateString) => formatDateIso(dateString, 'N/A');

  const formatDateTime = (d) => formatDateTimeTz(d, '');

  const getLambdaColor = (lambda) => {
    switch (lambda) {
      case 'oxi-enqueue': return 'log-badge-purple';
      case 'opera-oxi': return 'log-badge-blue';
      case 'oracle-ohip': return 'log-badge-orange';
      case 'sync-pep-processing': return 'log-badge-green';
      default: return 'log-badge-gray';
    }
  };

  const getDirectionColor = (direction) => {
    if (direction === 'entry') return 'log-badge-green';
    if (direction === 'poll') return 'log-badge-blue';
    if (direction === 'error') return 'log-badge-error';
    return 'log-badge-red';
  };

  const renderTaskItem = (task, group, fromTab = 'configuration') => {
    const destination = `/sync/${syncId}/task/${task.syncProjectTaskId || task.syncProjectTaskUuid}/configure?from=${fromTab}`;
    // The task status is kept only on Configuration, where "Pending vs Complete" is the whole point of
    // the screen: it tells you what is left to set up. On the data tabs the per-scope table below is the
    // real state, and a static "PENDING" over the top of live counts is noise that contradicts them.
    const showStatus = fromTab === 'configuration';
    const summary = taskSummaries[task.syncProjectTaskUuid];

    // On the data tabs, once the per-scope table has loaded it IS the content of the card: the task
    // name and the table share one row. The descriptive prose and the Automatic/Repeatable pills are
    // dropped there - they are fixed properties of the task definition, identical on every run, so on
    // a screen you scan for live counts they are the same words repeated down the page. Both are still
    // shown on Configuration, where deciding what a task does is the point of the screen.
    const inlineSummary = fromTab === 'tasks' && summary?.length > 0;

    return (
      <div
        key={task.syncProjectTaskUuid}
        className={`task-item task-item-clickable${inlineSummary ? ' task-item-inline' : ''}`}
        onClick={() => navigate(destination)}
      >
        {/* Badges sit on the title row rather than in a row of their own below the description. That
            row held two short pills and a timestamp and cost every task ~30px of height. */}
        <div className="task-header">
          <div className="task-title-row">
            {/* The sequence number is dropped on the data tabs: the cards are already rendered in
                sequence order, so it numbered a list that was visibly in order. It stays on
                Configuration, where the number is the order you work through the setup steps. */}
            {!inlineSummary && <span className="task-sequence">#{task.taskSequence}</span>}
            <h3 className="task-name">{task.taskName}</h3>
            {!inlineSummary && task.automaticYn && <span className="task-badge task-badge-blue">Automatic</span>}
            {!inlineSummary && task.repeatable && <span className="task-badge task-badge-green">Repeatable</span>}
            {/* Disabled survives the compact layout: it changes whether the task runs at all, so it is
                not decoration in the way the other two are. */}
            {!task.taskEnabled && <span className="disabled-badge">Disabled</span>}
            {/* On the data tabs the stamp sits UNDER the task name, inside the fixed-width title
                column, instead of in a block on the far right. It was the only thing in that block
                here (the status badge is Configuration-only), and it was taking ~230px off the
                middle of the row - enough that the stats table lost its PROGRESS column and had to
                scroll. Under the name it costs one line of a card that is already two lines tall
                for the table, and the table gets the whole width back. */}
            {inlineSummary && task.lastMessageAt && (
              <span className="task-last-message task-last-message-stacked">
                Last message: {formatDateTime(task.lastMessageAt)}
              </span>
            )}
          </div>

          {inlineSummary && (
            <div className="task-summary-inline">
              <TaskSummaryStats summary={summary} compact />
            </div>
          )}

          {((task.lastMessageAt && !inlineSummary) || showStatus) && (
            <div className="task-actions">
              {task.lastMessageAt && !inlineSummary && (
                <span className="task-last-message">Last message: {formatDateTime(task.lastMessageAt)}</span>
              )}
              {showStatus && (
                <span className={`status-badge ${getStatusColor(task.taskStatus)}`}>
                  {getStatusDisplay(task.taskStatus)}
                </span>
              )}
            </div>
          )}
        </div>

        {!inlineSummary && task.taskDescription && <p className="task-description">{task.taskDescription}</p>}
      </div>
    );
  };

  // A task a person performs in the target system rather than one the pipeline runs.
  //
  // Detected from a "[MANUAL]" marker in the description because that is the ONLY signal the data
  // carries today: synctemplatetasks.automaticyn is 'N' on every row of the OPERA2OPERA template,
  // manual and automated alike, so it cannot be used. Matching on display text is fragile - editing
  // a description silently changes which tasks are hidden - so it is confined to this one function,
  // ready to become a column read when there is one. See the note in the handover.
  const isManualTask = (task) => /\[MANUAL\]/i.test(task.taskDescription || '');

  // A task has messages/records flowing through it if its reconciliation summary has any records, or
  // it has ever recorded a last-message timestamp (covers tasks whose summary hasn't loaded yet).
  const taskHasMessages = (task) => {
    const s = taskSummaries[task.syncProjectTaskUuid];
    if (Array.isArray(s) && s.some(item => (parseInt(item.totalRecords, 10) || 0) > 0)) return true;
    return !!task.lastMessageAt;
  };

  // Configuration tab: flat list, no card header, no expand
  const renderConfigTaskGroups = (groups, fromTab = 'configuration', searchFilter = '') => {
    const q = searchFilter.trim().toLowerCase();
    const allTasks = groups
      .sort((a, b) => a.sequence - b.sequence)
      .flatMap(group => getTasksByGroup(group.groupName).map(task => ({ task, group })))
      .filter(({ task }) => !q || task.taskName?.toLowerCase().includes(q) || task.taskDescription?.toLowerCase().includes(q))
      // "Only with messages" applies on the tasks tab: hide tasks with no records/activity.
      .filter(({ task }) => !(fromTab === 'tasks' && onlyWithMessages) || taskHasMessages(task))
      // Manual tasks are hidden on the tasks tab unless asked for. Deliberately NOT applied to the
      // other tabs: on Configuration a manual step is something you still have to go and do.
      .filter(({ task }) => !(fromTab === 'tasks' && !showManualTasks) || !isManualTask(task));
    if (allTasks.length === 0) return null;
    return (
      <Card className="task-group-card">
        <div className="tasks-list tasks-list-flat">
          {allTasks.map(({ task, group }) => renderTaskItem(task, group, fromTab))}
        </div>
      </Card>
    );
  };

  // Extract & Load tab: collapsible groups with header
  const renderTaskGroups = (groups) => {
    const sorted = groups.sort((a, b) => a.sequence - b.sequence);
    return sorted.map((group) => {
      const tasks = getTasksByGroup(group.groupName);
      if (tasks.length === 0) return null;
      const isExpanded = expandedTaskGroups.has(group.groupName);
      return (
        <Card key={group.groupName} className="task-group-card">
          <div className="card-header expandable" onClick={() => toggleTaskGroup(group.groupName)}>
            <h2 className="card-title">{group.groupDescription}</h2>
            <ChevronDown className={`chevron-icon ${isExpanded ? 'expanded' : ''}`} />
          </div>
          {isExpanded && (
            <div className="tasks-list">
              {tasks.map((task) => renderTaskItem(task, group, 'extract'))}
            </div>
          )}
        </Card>
      );
    });
  };

  if (loading) {
    return (
      <div className="sync-project-details-page">
        <div className="sync-details-container">
          <div className="loading-container">
            <Spinner size="lg" />
            <p className="loading-text">Loading project details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !projectDetails) {
    return (
      <div className="sync-project-details-page">
        <div className="sync-details-container">
          <div className="error-message">{error || 'Project not found'}</div>
          <Button variant="secondary" onClick={() => navigate('/sync')}>
            <ArrowLeft className="icon" /> Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  // Block the entire project page when payment hasn't landed yet (invoice) or subscription was cancelled.
  if (projectDetails.paid !== 'Y' && projectDetails.stripeInvId) {
    return (
      <AwaitingPaymentBlock
        projectDetails={projectDetails}
        syncId={syncId}
        navigate={navigate}
        isSuperAdmin={isSuperAdmin}
        onMarkedPaid={loadProjectDetails}
      />
    );
  }

  // Subscription cancelled — access blocked, show a notice to contact their admin.
  if (projectDetails.paid !== 'Y' && projectDetails.stripeSubId) {
    return (
      <div className="sync-project-details-page">
        <div className="sync-details-container">
          <div className="breadcrumb">
            <span className="breadcrumb-link" style={{ cursor: 'pointer' }} onClick={() => navigate('/sync')}>Sync Projects</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{projectDetails.projectName || 'Project'}</span>
          </div>
          <div style={{ marginTop: '2rem', padding: '24px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', color: '#991b1b', maxWidth: '600px' }}>
            <strong style={{ fontSize: '15px' }}>⛔ Sync project access has been suspended</strong>
            <p style={{ margin: '10px 0 0' }}>The subscription for this sync project has been cancelled or a payment has failed. Please contact your sync administrator to set up a new payment and restore access.</p>
          </div>
        </div>
      </div>
    );
  }

  const configGroups   = taskGroups.filter(g => isConfigGroup(g.groupName));
  const extractGroups  = taskGroups.filter(g => isExtractGroup(g.groupName));
  const processGroups  = taskGroups.filter(g => isProcessGroup(g.groupName));
  const scheduleGroups = taskGroups.filter(g => isScheduleGroup(g.groupName));
  // Any groups not categorised fall into extract tab
  const otherGroups    = taskGroups.filter(g => !isConfigGroup(g.groupName) && !isExtractGroup(g.groupName) && !isProcessGroup(g.groupName) && !isScheduleGroup(g.groupName));

  // ── The pipeline, built from what this project actually is ────────────────────────────────────
  // Source <-> [SyncConnect] <-> hconnect Hub <-> Target. The hub was previously invisible even though
  // every heartbeat on this strip is measured against it, and SyncConnect was reduced to a button, so a
  // single source->target arrow was drawn over a path with up to two more hops in it.
  const viaSyncConnect = projectDetails.usesSyncConnect === 'Y';

  // Both source heartbeats measure the property end's contact WITH THE HUB. When SyncConnect is
  // installed it is the thing holding that connection - OPERA/OXI only ever talks to SyncConnect on
  // the loopback - so the badges belong on the SyncConnect card and OPERA/OXI carries none.
  const sourceBadges = renderContactBadges('Source', projectDetails.sourceHasEnqueue, projectDetails.sourceHasDequeue, false);

  const pipelineNodes = [
    {
      key: 'source',
      label: 'Source',
      name: projectDetails.sourceSystemName,
      hint: projectDetails.sourceConnectorName
        ? `${projectDetails.sourceSystemName} (${projectDetails.sourceConnectorName})`
        : projectDetails.sourceSystemName,
      image: projectDetails.sourceSystemImage,
      icon: Database,
      badges: viaSyncConnect ? null : sourceBadges,
      alert: viaSyncConnect ? statusChecks?.source || null : null,
    },
    ...(viaSyncConnect ? [{
      key: 'syncconnect',
      label: 'Client',
      name: 'SyncConnect',
      hint: 'SyncConnect desktop client, installed at the property',
      // MonitorDown: software you download and install on a machine at the property. The download arrow
      // also ties the icon to the Generate/Download SyncConnect action on this same card.
      icon: MonitorDown,
      badges: sourceBadges,
      alert: statusChecks?.client || null,
      children: (
        <SyncConnectClientButton
          syncId={syncId}
          initialStatus={projectDetails.syncConnectStatus}
          isSuperAdmin={isSuperAdmin}
          offline={projectDetails.syncConnectOffline === 'Y'}
        />
      ),
    }] : []),
    {
      key: 'hub',
      label: 'Platform',
      name: 'Hub by hconnect',
      hint: 'Every heartbeat on this strip is contact with the hub',
      // The real brand mark, same component as the sidebar and landing page - the hub is the one node
      // in this chain that is us, so it should look like the product the user is already logged into.
      mark: <HubLogoMark size={28} />,
      badges: renderCutoverBadge(),
    },
    {
      key: 'target',
      label: 'Target',
      name: projectDetails.targetSystemName,
      hint: projectDetails.targetConnectorName
        ? `${projectDetails.targetSystemName} (${projectDetails.targetConnectorName})`
        : projectDetails.targetSystemName,
      image: projectDetails.targetSystemImage,
      icon: Database,
      badges: (
        <>
          {renderContactBadges('Target', projectDetails.targetHasEnqueue, projectDetails.targetHasDequeue, true)}
          {renderBusinessDateBadge()}
        </>
      ),
    },
  ];

  return (
    <div className="sync-project-details-page">
      <div className="sync-details-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/sync" className="breadcrumb-link">Sync Projects</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {projectDetails?.projectName || 'Project'}
            {projectDetails?.hotelName && <span style={{ fontWeight: 400, color: '#6b7280' }}> ({projectDetails.hotelName})</span>}
          </span>
        </div>

        {/* Header — title, actions, systems and heartbeats in one band.
            These used to be three stacked blocks totalling ~480px, so on a laptop the task list (the
            reason for visiting the page) began below the fold. Same information, ~200px. */}
        <div className="details-header">
          <div className="header-top-row">
            <Button variant="secondary" onClick={() => navigate('/sync')} className="back-button">
              <ArrowLeft className="icon" /> Back
            </Button>
            {/* Title moved up beside the actions instead of sitting on its own row below them —
                the row was mostly empty space between Back and Refresh. */}
            <div className="header-title-block">
              <h1 className="details-title">{projectDetails.projectName}</h1>
              {/* Paid sits with the title, not in the pipeline strip. It is a fact about the PROJECT,
                  not about any hop in the path, and parking it at the end of the strip left it looking
                  like a fifth node. */}
              <div className="details-subtitle-row">
                {projectDetails.templateName && (
                  <p className="details-subtitle">{projectDetails.templateName}</p>
                )}
                {projectDetails.paid === 'Y' && (
                  <span className="sync-paid-badge" title="Payment confirmed">
                    <CheckCircle size={16} strokeWidth={2.25} />
                    Paid
                  </span>
                )}
              </div>
            </div>
            <div className="header-right">
              <div className="status-toggle-group">
                {['active', 'inactive'].map(s => (
                  <button
                    key={s}
                    className={`status-toggle-btn${projectDetails.projectStatus === s ? ' active' : ''}`}
                    onClick={() => handleStatusChange(s)}
                    disabled={updatingStatus || projectDetails.projectStatus === s || (projectDetails.paid !== 'Y' && projectDetails.stripeInvId)}
                    title={projectDetails.paid !== 'Y' && projectDetails.stripeInvId ? 'Awaiting payment confirmation from Stripe' : undefined}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              {/* The less-used actions fold behind a chevron (shared/CollapsibleActions) so the header is not a row of
                  seven buttons squeezing the project name; the status switch and Refresh stay in view. */}
              <CollapsibleActions storageKey="sync-project-header">
              {/* SUPERADMIN ONLY. The project-level on-site report covers every extraction task that
                  has records — one list for the property instead of a task at a time — and it is the
                  report that can be put on a daily schedule. Marked sa-only per STYLE.md. */}
              {isSuperAdmin && (
                <Button variant="secondary" className="sa-only" onClick={() => navigate(`/sync/${syncId}/reports`)}>
                  <FileText className="icon" /> Reports
                </Button>
              )}
              {/* The Migration Toolkit for this project — only on templates that use it (uses_migration_toolkit = Y). It
                  works through this project's own OPERA Cloud connection. Superadmin, as the Toolkit always was. */}
              {isSuperAdmin && projectDetails.usesMigrationToolkit === 'Y' && (
                <Button variant="secondary" className="sa-only" onClick={() => navigate(`/sync/${syncId}/toolkit`)}>
                  <Wrench className="icon" /> Toolkit
                </Button>
              )}
              <Button variant="secondary" onClick={() => navigate(`/sync/${syncId}/edit`)}>
                <Edit className="icon" /> Edit
              </Button>
              {/* The template's documentation page (synctemplates.docs_page_id, V622), live from Confluence. */}
              <DocsButton pageId={projectDetails.docsPageId} />
              </CollapsibleActions>
              <Button variant="success" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className={`icon ${refreshing ? 'spinning' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Awaiting-payment banner — shown when an invoice was issued but Stripe hasn't confirmed it yet.
              The project's wizard progression is saved on syncprojects.wizardstep so the user can leave and return at any time. */}
          {projectDetails.paid !== 'Y' && projectDetails.stripeInvId && (
            <div style={{ marginTop: '12px', padding: '12px 16px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>⏳</span>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '2px' }}>Awaiting payment</div>
                <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                  Your invoice has been emailed. The project's setup is saved — you can close this tab and come back any time.
                  The project will become active automatically once Stripe confirms the payment, and sync tasks are paused until then.
                </div>
              </div>
            </div>
          )}

          {/* SPR init result banner */}
          {sprInitResult && (
            <div className={`spr-init-result ${sprInitResult.success ? 'spr-init-success' : 'spr-init-error'}`}>
              {sprInitResult.success ? (
                <>
                  <CheckCircle className="spr-init-icon" />
                  <span>SPR storage initialised. Path: <code>{sprInitResult.data?.sprPath}</code></span>
                </>
              ) : (
                <>
                  <AlertCircle className="spr-init-icon" />
                  <span>{sprInitResult.message}</span>
                </>
              )}
              <button className="spr-init-close" onClick={() => setSprInitResult(null)}><X size={14} /></button>
            </div>
          )}
        </div>

        {/* Systems + heartbeats strip.
            The source→target pair and the live contact badges now share one row: they are read
            together ("is the right pair wired up, and is it talking?") and neither needs a panel of
            its own. Left half stays the click target for the full details. */}
        <Card className="project-details-card">
          <div className="sync-strip">
            {/* Not a <button> wrapping the whole row: the SyncConnect control below is itself a button,
                and a button inside a button is invalid and swallows the inner click. The chevron is the
                toggle instead. */}
            <div className="sync-systems">
              {pipelineNodes.map((node, i) => (
                <React.Fragment key={node.key}>
                  {i > 0 && <ArrowLeftRight className="sync-node-arrow" />}
                  <PipelineNode {...node} />
                </React.Fragment>
              ))}
              <button
                type="button"
                className="sync-expand-btn"
                onClick={() => setIsProjectDetailsExpanded(!isProjectDetailsExpanded)}
                aria-expanded={isProjectDetailsExpanded}
                aria-label={isProjectDetailsExpanded ? 'Hide project details' : 'Show project details'}
                title={isProjectDetailsExpanded ? 'Hide project details' : 'Show project details'}
              >
                <ChevronDown className={`chevron-icon systems-chevron ${isProjectDetailsExpanded ? 'expanded' : ''}`} />
              </button>
            </div>
          </div>

          {isProjectDetailsExpanded && (
            <div className="expanded-content">
              <div className="detail-section">
                <h3 className="section-subtitle">Template Information</h3>
                {resolvedLevel && (
                  <div className="resolved-source-bar">
                    {resolvedLevel === 'hotel'    && <span className="resolved-badge resolved-hotel">Customised for this hotel — {resolvedTemplateName}</span>}
                    {resolvedLevel === 'account'  && <span className="resolved-badge resolved-account">Customised for this chain — {resolvedTemplateName}</span>}
                    {resolvedLevel === 'default'  && <span className="resolved-badge resolved-default">Partner default — {resolvedTemplateName}</span>}
                    {resolvedLevel === 'platform' && <span className="resolved-badge resolved-platform">Platform default tasks</span>}
                  </div>
                )}
                <div className="detail-grid">
                  <div className="detail-item">
                    <div><div className="detail-label">Template Name</div><div className="detail-value">{projectDetails.templateName}</div></div>
                  </div>
                  <div className="detail-item">
                    <div><div className="detail-label">Description</div><div className="detail-value">{projectDetails.templateDescription || 'N/A'}</div></div>
                  </div>
                  <div className="detail-item">
                    <div>
                      <div className="detail-label">Chain</div>
                      <div className="detail-value">
                        {projectDetails.accountName
                          ? `${projectDetails.accountName}${projectDetails.accountId ? ` (${projectDetails.accountId})` : ''}`
                          : projectDetails.accountId || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div>
                      <div className="detail-label">Hotel</div>
                      <div className="detail-value">
                        {projectDetails.hotelName
                          ? `${projectDetails.hotelName}${projectDetails.hotelId ? ` (${projectDetails.hotelId})` : ''}`
                          : projectDetails.hotelId || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div><div className="detail-label">Property ID</div><div className="detail-value">{projectDetails.propertyId || 'N/A'}</div></div>
                  </div>
                  <div className="detail-item">
                    <div><div className="detail-label">Sync Project ID</div><div className="detail-value">{projectDetails.syncProjectId || 'N/A'}</div></div>
                  </div>
                  <div className="detail-item">
                    <div>
                      <div className="detail-label">Project Status</div>
                      <div className="detail-value">
                        <span className={`status-badge ${getStatusColor(projectDetails.projectStatus)}`}>{getStatusDisplay(projectDetails.projectStatus)}</span>
                      </div>
                    </div>
                  </div>
                  {/* Stated explicitly rather than left to be inferred from whether a SyncConnect card
                      happens to appear in the pipeline above. "Disabled" is a real answer here - the
                      absence of a card looks the same as a page that has not finished loading. */}
                  <div className="detail-item">
                    <div>
                      <div className="detail-label">SyncConnect</div>
                      <div className="detail-value">
                        <span className={`status-badge ${viaSyncConnect ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {viaSyncConnect ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="detail-section">
                <h3 className="section-subtitle">Project Dates</h3>
                <div className="detail-grid">
                  <div className="detail-item"><Calendar className="detail-icon" /><div><div className="detail-label">Created</div><div className="detail-value">{formatDate(projectDetails.insertDate)}</div></div></div>
                  <div className="detail-item"><Calendar className="detail-icon" /><div><div className="detail-label">Updated</div><div className="detail-value">{formatDate(projectDetails.updateDate)}</div></div></div>
                  <div className="detail-item"><Calendar className="detail-icon" /><div><div className="detail-label">Cutover Date</div><div className="detail-value">{formatDate(projectDetails.projectCutoverDate)}</div></div></div>
                </div>
              </div>
              {(projectDetails.contactName || projectDetails.contactEmail || projectDetails.contactPhone) && (
                <div className="detail-section">
                  <h3 className="section-subtitle">Contact Information</h3>
                  <div className="detail-grid">
                    {projectDetails.contactName && <div className="detail-item"><User className="detail-icon" /><div><div className="detail-label">Name</div><div className="detail-value">{projectDetails.contactName}</div></div></div>}
                    {projectDetails.contactEmail && <div className="detail-item"><Building className="detail-icon" /><div><div className="detail-label">Email</div><div className="detail-value">{projectDetails.contactEmail}</div></div></div>}
                    {projectDetails.contactPhone && <div className="detail-item"><Building className="detail-icon" /><div><div className="detail-label">Phone</div><div className="detail-value">{projectDetails.contactPhone}</div></div></div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Tabs */}
        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'configuration' ? 'active' : ''}`} onClick={() => handleTabChange('configuration')}>Configuration</button>
          <button className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => handleTabChange('tasks')}>Extraction</button>
          <button className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => handleTabChange('schedule')}>Scheduled Tasks</button>
          <button className={`tab-btn ${activeTab === 'process' ? 'active' : ''}`} onClick={() => handleTabChange('process')}>Process</button>

          {isSuperAdmin && (
            <button className={`tab-btn sa-only ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => handleTabChange('logs')}>Logs</button>
          )}
          <button className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => handleTabChange('notifications')}>Notifications</button>
          <button className={`tab-btn ${activeTab === 'support' ? 'active' : ''}`} onClick={() => handleTabChange('support')}>Support</button>
          {isSuperAdmin && (
            <button className={`tab-btn sa-only ${activeTab === 'backups' ? 'active' : ''}`} onClick={() => handleTabChange('backups')}>Backups</button>
          )}
          {/* WHAT WE HAVE ASKED THE SOURCE FOR. Only for a source that collects its instructions from us
              (has_dequeue — OXI / SyncConnect): anywhere else the queue is empty by definition and the
              tab would be a dead end. Superadmin-only, like Logs: the rows carry the instruction bodies
              and the responses, which is support material rather than something a property is handed. */}
          {isSuperAdmin && projectDetails?.sourceHasDequeue === 'Y' && (
            <button className={`tab-btn sa-only ${activeTab === 'instructions' ? 'active' : ''}`} onClick={() => handleTabChange('instructions')}>Instructions to Source</button>
          )}
        </div>

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="tab-content">
            {renderTasksTab()}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div className="tab-content">
            {configGroups.length === 0 ? (
              <Card className="task-group-card">
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ margin: 0 }}>No configuration tasks found for this project.</p>
                </div>
              </Card>
            ) : (
              <div className="tasks-section">
                {renderConfigTaskGroups(configGroups, 'configuration')}
              </div>
            )}
          </div>
        )}

        {/* Process Tab */}
        {activeTab === 'process' && (
          <div className="tab-content">
            {processGroups.length === 0 ? (
              <Card className="task-group-card">
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ margin: 0 }}>No process tasks found for this project.</p>
                </div>
              </Card>
            ) : (
              <div className="tasks-section">
                {renderConfigTaskGroups(processGroups, 'process')}
              </div>
            )}
          </div>
        )}


        {/* Scheduled Tasks Tab */}
        {activeTab === 'schedule' && (
          <div className="tab-content">
            {scheduleGroups.length === 0 ? (
              <Card className="task-group-card">
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ margin: 0 }}>No scheduled tasks found for this project.</p>
                </div>
              </Card>
            ) : (
              <div className="tasks-section">
                {renderConfigTaskGroups(scheduleGroups, 'schedule')}
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'support' && (
          <Card>
            <ProductSupportTab product="Sync" productId={syncId} noun="this sync project"
              context={{ screen: 'sync-project', syncId, projectName: projectDetails.projectName, template: projectDetails.templateName }} />
          </Card>
        )}

        {activeTab === 'notifications' && (
          <div className="tab-content">
            <Card className="task-group-card">
              <div style={{ padding: '1.5rem' }}>
                <h2 className="card-title" style={{ marginTop: 0 }}>Notifications</h2>
                <p className="form-hint" style={{ marginBottom: '1.25rem' }}>
                  Manage who receives error alerts and a summary of this project's sync activity. Changes are saved immediately.
                </p>
                <NotificationsPanel
                  value={notif}
                  onChange={setNotif}
                  hotelId={projectDetails?.hotelId}
                  accountId={projectDetails?.accountId}
                  persistence={notifPersistence}
                />
              </div>
            </Card>
          </div>
        )}

        {/* Backups Tab — superadmin only (data served by the admin GW) */}
        {activeTab === 'backups' && isSuperAdmin && (
          <div className="tab-content">
            <Card className="task-group-card sa-only-panel">
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h2 className="card-title" style={{ margin: 0 }}>Index Backups</h2>
                  <Button variant="secondary" onClick={loadBackups} disabled={backupsLoading}>
                    <RefreshCw className={`icon ${backupsLoading ? 'spinning' : ''}`} /> Refresh
                  </Button>
                </div>
                <p className="form-hint" style={{ marginBottom: '1.25rem' }}>
                  When a project has no activity for 30 days its reconciliation index is archived to S3 and the project is
                  set inactive. Restoring re-indexes the data, resets the activity clock, and re-activates the project.
                </p>
                {backupsError && (
                  <div style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem' }}>{backupsError}</div>
                )}
                {backupsLoading ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}><Spinner size="sm" /> Loading backups…</div>
                ) : backups.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    <p style={{ margin: 0 }}>No backups for this project.</p>
                  </div>
                ) : (
                  <table className="backups-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '0.5rem 0.75rem' }}>Backed Up</th>
                        <th style={{ padding: '0.5rem 0.75rem' }}>Records</th>
                        <th style={{ padding: '0.5rem 0.75rem' }}>Status</th>
                        <th style={{ padding: '0.5rem 0.75rem' }}>Storage</th>
                        <th style={{ padding: '0.5rem 0.75rem' }}>Restored</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.map(b => (
                        <tr key={b.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{b.backedUpAt ? formatDateTime(b.backedUpAt) : '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{(b.docCount ?? 0).toLocaleString()}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{b.status}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{b.storageTier === 'GLACIER' ? 'Glacier' : 'Standard'}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{b.restoredAt ? formatDateTime(b.restoredAt) : '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                            <Button
                              variant="secondary"
                              onClick={() => setRestoreConfirm(b)}
                              disabled={restoringId === b.id || b.status === 'RESTORING'}
                            >
                              {restoringId === b.id ? (<><Spinner size="sm" /> Restoring…</>) : (<><Upload className="icon" /> Restore</>)}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Instructions to Source Tab — the project's own view of the dequeue queue and its history.
            Same component as /admin/oxi-dequeue-queue, scoped to this project and with nothing on it
            that deletes. */}
        {activeTab === 'instructions' && isSuperAdmin && (
          <div className="tab-content">
            <Card className="sa-only-panel">
              <div style={{ padding: '0 1.25rem', color: '#6b7280', fontSize: '0.875rem' }}>
                <p style={{ margin: '1rem 0 0' }}>
                  Every instruction this project has put in front of its source system: <strong>Queue</strong> is
                  waiting to be collected, <strong>History</strong> is what has already been handed over — open a
                  row for the exact message sent and whatever came back. Kept for at least four weeks.
                </p>
                {projectDetails?.sourceHasDequeue !== 'Y' && (
                  <p style={{ margin: '0.5rem 0 0', color: '#92400e' }}>
                    This project's source does not collect instructions from the hub, so there will be nothing here.
                  </p>
                )}
              </div>
              <OxiDequeueAdmin syncProjectId={syncId} />
            </Card>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && isSuperAdmin && (
          <div className="tab-content">
            <Card className="logs-card sa-only-panel">
              <div className="logs-header">
                <div className="logs-header-actions">
                  <Button variant="secondary" onClick={() => loadLogs(logFilters)} disabled={logsLoading}>
                    <RefreshCw className={`icon ${logsLoading ? 'spinning' : ''}`} /> Refresh
                  </Button>
                  {purgeConfirm ? (
                    <div className="purge-confirm">
                      <span>Delete all logs?</span>
                      <Button variant="danger" onClick={handlePurgeLogs} disabled={purgeLoading}>Yes, purge</Button>
                      <Button variant="secondary" onClick={() => setPurgeConfirm(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="secondary" onClick={handlePurgeLogs} disabled={purgeLoading}>
                      <X className="icon" /> Purge Logs
                    </Button>
                  )}
                </div>
              </div>
              <form className="logs-search-bar" onSubmit={handleLogSearch}>
                <div className="logs-filter-group">
                  <label className="logs-filter-label">Transaction ID</label>
                  <input
                    type="text"
                    className="logs-filter-input"
                    placeholder="Filter by transaction ID..."
                    value={logFilters.transactionId}
                    onChange={(e) => setLogFilters(f => ({ ...f, transactionId: e.target.value }))}
                  />
                </div>
                <div className="logs-filter-group">
                  <label className="logs-filter-label">Lambda</label>
                  <select className="logs-filter-input" value={logFilters.lambda} onChange={(e) => setLogFilters(f => ({ ...f, lambda: e.target.value }))}>
                    <option value="">All</option>
                    <option value="oxi-enqueue">oxi-enqueue</option>
                    <option value="opera-oxi">opera-oxi</option>
                    <option value="oracle-ohip">oracle-ohip</option>
                    <option value="sync-pep-processing">sync-pep-processing</option>
                  </select>
                </div>
                <div className="logs-filter-group">
                  <label className="logs-filter-label">Direction</label>
                  <select className="logs-filter-input" value={logFilters.direction} onChange={(e) => setLogFilters(f => ({ ...f, direction: e.target.value }))}>
                    <option value="">All</option>
                    <option value="entry">Entry</option>
                    <option value="exit">Exit</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <Button type="submit" variant="primary" disabled={logsLoading}>
                  <Search className="icon" /> Search
                </Button>
              </form>

              {logsLoading && (
                <div className="logs-loading"><Spinner size="sm" /><span>Loading logs...</span></div>
              )}
              {logsError && <div className="error-message">{logsError}</div>}
              {!logsLoading && !logsError && logsLoaded && logs.length === 0 && (
                <div className="logs-empty">No message logs found for this project. Logs appear after the first message is processed.</div>
              )}
              {!logsLoading && logs.length > 0 && (
                <div className="logs-table-wrapper">
                  <table className="logs-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Transaction ID</th>
                        <th>Lambda</th>
                        <th>Direction</th>
                        <th>Payload</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, idx) => (
                        <React.Fragment key={idx}>
                          <tr className={`log-row ${expandedLog === idx ? 'log-row-expanded' : ''} ${log.direction === 'error' || log.data?.status === 'error' ? 'log-row-error' : ''}`} onClick={() => setExpandedLog(expandedLog === idx ? null : idx)}>
                            <td className="log-cell-time">{formatDateTime(log.lastModified)}</td>
                            <td className="log-cell-txn"><code className="log-txn">{log.transactionId}</code></td>
                            <td><span className={`log-badge ${getLambdaColor(log.lambda)}`}>{log.lambda}</span></td>
                            <td><span className={`log-badge ${getDirectionColor(log.direction)}`}>{log.direction}</span></td>
                            <td className="log-cell-payload" onClick={(e) => e.stopPropagation()}>
                              {log.data?.data?.payloadB64 && (
                                <button className="payload-btn" onClick={(e) => handleViewPayload(e, log.data.data.payloadB64)}>
                                  View Payload
                                </button>
                              )}
                            </td>
                            <td className="log-cell-action">
                              <ChevronDown className={`chevron-icon small ${expandedLog === idx ? 'expanded' : ''}`} />
                            </td>
                          </tr>
                          {expandedLog === idx && (
                            <tr className="log-detail-row">
                              <td colSpan={6}>
                                {log.data ? (
                                  <pre className="log-detail-pre">{JSON.stringify(
                                    {
                                      ...log.data,
                                      data: log.data.data
                                        ? Object.fromEntries(Object.entries(log.data.data).filter(([k]) => k !== 'payloadB64'))
                                        : log.data.data
                                    },
                                    null, 2
                                  )}</pre>
                                ) : (
                                  <div className="log-detail-pre" style={{ color: '#9ca3af', fontStyle: 'italic' }}>No detail data available for this log entry.</div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <p className="logs-count">Showing {logs.length} log {logs.length === 1 ? 'entry' : 'entries'} (max 100)</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Payload Dialog */}
      {payloadDialog && (
        <div className="payload-dialog-overlay" onClick={() => setPayloadDialog(null)}>
          <div className="payload-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="payload-dialog-header">
              <div className="payload-dialog-title-row">
                <span className="payload-dialog-title">Message Payload</span>
                <span className={`payload-type-badge payload-type-${payloadDialog.type}`}>{payloadDialog.type.toUpperCase()}</span>
              </div>
              <div className="payload-dialog-actions">
                <button className="payload-format-btn" onClick={togglePayloadFormat}>
                  {payloadDialog.isPretty ? 'Raw' : 'Pretty Print'}
                </button>
                <button className="payload-dialog-close" onClick={() => setPayloadDialog(null)}><X size={16} /></button>
              </div>
            </div>
            <pre className="payload-dialog-pre">{payloadDialog.content}</pre>
          </div>
        </div>
      )}

      {restoreConfirm && (
        <ConfirmModal
          title="Restore backup"
          message={`Restore this project's index from the backup taken ${restoreConfirm.backedUpAt ? formatDateTime(restoreConfirm.backedUpAt) : ''}? This re-indexes ${(restoreConfirm.docCount ?? 0).toLocaleString()} record(s), resets the activity clock, and re-activates the project.`}
          confirmLabel="Restore"
          cancelLabel="Cancel"
          onConfirm={() => { const b = restoreConfirm; setRestoreConfirm(null); handleRestoreBackup(b); }}
          onCancel={() => setRestoreConfirm(null)}
        />
      )}
    </div>
  );
};

export default SyncProjectDetailsPage;
